import { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { IPageState, PageContext } from '../../contexts/PageContext';
import { BUY_BACK_STEPS, CURRENCIES, PATHS, TRANSACTION_STATUS, TRANSACTION_TYPES, WEIGHT_UNITS } from '../../utils/constant';
import Inbound from '../$Inbound';
import Billing  from '../$Billing';
import Summary from '../$Summary';
import { formatDate, getFormattedCurrency, getThreePointNumber, getTwoPointNumber, objToArray } from '../../utils/helpers';
import SelectCustomer from '../$SelectCustomer';
import { TTransactionBasic } from '../../Models/Transactions';
import { TransactionContext } from '../../contexts/TransactionContext';
import { AuthContext } from '../../contexts/AuthContext';
import { CustomerContext } from '../../contexts/CustomerContext';
import useService from '../../hooks/useService';
import {fetchCharges, getLotDetails, getTransaction as getTransactionService} from '../../services/Transactions'
import HighlightsCardAndCustomerDetails from '../custom/HighlightsCardAndCustomerDetails';


export type TBuyBackSectionKeys = 'INBOUND' |'BILLING' | 'SUMMARY' //BUY BACK breadCrum

const PAGE_INFO: IPageState = {
    title: 'Buy Back',
    breadcrumbs: objToArray(BUY_BACK_STEPS).map(item => ({path: item.key, label: item.value})),
    currentPage: BUY_BACK_STEPS.INBOUND,
    path: PATHS.BUY_BACK
}

type TBuybackSummaryKeys = 'customerId' 
| 'customerName' 
| 'pvcName' 
| 'transactionDate' 
| 'totalGoldWeight' 
| 'totalSilverWeight'
| 'totalWBMGold'
| 'totalWAMGold'
| 'totalWAMTGold'
| 'settlementFineWeight'
| 'totalTaxValue'
| 'totalCharges'
| 'roundingRs'
| 'totalValue'
| 'totalMetalReturnNetWeight'

const SUMMARY_DETAILS: {id: TBuybackSummaryKeys, label: any, value: any}[] = [
    {
        id: 'customerId',
        label:"Customer Id",
        value : ""
    },
    {
        id:"customerName",
        label:"Customer Name",
        value : ""
    },
    {
        id:"pvcName",
        label:"PVC Name",
        value : ""
    },
    {
        id:"transactionDate",
        label:"Transaction Date",
        value : ""
    },
    {
        id:"totalGoldWeight",
        label:"Gold Weight",
        value : ""
    },
    {
        id:"totalSilverWeight",
        label:"Silver Weight",
        value : ""
    },
    {
        id:'totalWBMGold',
        label:"Total WBM",
        value : ''
    },
    {
        id:'totalWAMGold',
        label:"Total WAM",
        value : ''
    },
    {
        id:'totalWAMTGold',
        label:"Total WAMT",
        value : ''
    },
    {
        id:'settlementFineWeight',
        label:"Settlement Fine Wt.",
        value : ''
    },
    {
        id:'totalMetalReturnNetWeight',
        label:"Total Metal Return Net Wt.",
        value : ''
    },
    {
        id:'totalTaxValue',
        label:"Total Tax Value Rs.",
        value : ''
    },
    {
        id:'totalCharges',
        label:"Total Charges Rs.",
        value : ''
    },
    {
        id:'roundingRs',
        label:"Rounding Rs.",
        value : ''
    },
    {
        id:'totalValue',
        label:"Grand Total Rs.",
        value : ''
    },

]


const Container = styled.div`
    width: 100%;
`

const getPageInfo = (section: TBuyBackSectionKeys) => {
    const pageInfo = {...PAGE_INFO, currentPage:section || 'INBOUND'}
    return pageInfo
}


const BuyBack = () => {

    const { setPageState } = useContext(PageContext)
    const { meltingLot, setTransactionInfo, transactionInfo: { transactionType, globalTransactionNumber, status, updatedAt }, inboundDetails, resetOrder, setCapturedSteps, pvcCenterDetails, physicalOrder } = useContext(TransactionContext)
    const { user } = useContext(AuthContext)
    const { customerConfig } = useContext(CustomerContext)
    const [searchParams] = useSearchParams()
    const { id } = useParams()
    const { execute } = useService()
    const navigate = useNavigate()
    const [showCustomerDetails,setShowCustomerDetails] = useState<boolean>(false)

    const step = searchParams.get('section')

    const getTransaction = async () => {
        const { data } = await execute(() => getTransactionService({ globalTransactionNumber: id!, transactionType }))
        if(data?.status === TRANSACTION_STATUS.COMPLETED) await execute(()=> getLotDetails({globalTransactionNumber:data?.globalTransactionNumber!}))
        if (data !== null) {
            switch (data.status) {
                case TRANSACTION_STATUS.INBOUND_INITIATED:
                    navigate(PATHS.BUY_BACK + '/' + id + '?section=INBOUND')
                    break;
                case TRANSACTION_STATUS.PAYMENT_INITIATED:
                    setCapturedSteps(['INBOUND'])
                    navigate(PATHS.BUY_BACK + '/' + id + '?section=BILLING')
                    break;
                case TRANSACTION_STATUS.COMPLETED:
                case TRANSACTION_STATUS.CANCELLED:
                case TRANSACTION_STATUS.PENDING_CANCELLATION:
                    setCapturedSteps(['INBOUND', 'BILLING'])
                    navigate(PATHS.BUY_BACK + '/' + id + '?section=SUMMARY')
                    break;
                default:
                    navigate(PATHS.BUY_BACK + '/' + id + '?section=INBOUND')
                    break;
            }

        }
    }


    const getCharges = () => {
        execute(()=>fetchCharges({globalTransactionNumber: globalTransactionNumber!, transactionType:transactionType}))
    }

    const getSummary = () => {
        let totalWBM = 0
        let totalWAM = 0
        let totalWAMT = 0
        let settlementFineWeight = 0
        let totalMetalReturnNetWeight = 0
        console.log('in summary...', meltingLot)
        meltingLot.forEach(lot => {
            console.log('lot in summary...')
            totalWBM += lot.netWeight
            totalWAM += lot.weightAfterMelting
            totalWAMT += lot.wamt
            settlementFineWeight += lot.fineWeightToReturn
            totalMetalReturnNetWeight += lot.finalSettlementNetWeight
        })
        let totalTaxValue = 0
        physicalOrder.taxes?.forEach((tax) => {
            totalTaxValue += tax.taxAmount
        })
        let totalCharges = 0
        physicalOrder.charges?.forEach((charge) => {
            totalCharges += charge.preTaxAmount
            totalTaxValue += charge.postTaxAmount - charge.preTaxAmount
        })
        const roundingRs = getTwoPointNumber((Math.round(physicalOrder.totalPrice || 0) - (physicalOrder.totalPrice || 0)) ?? 0)
        return SUMMARY_DETAILS.map(field => {
            switch (field.id) {
                case 'customerId':
                    field.value = customerConfig.customerRefNo
                    break;
                case 'customerName':
                    field.value = customerConfig.fullName
                    break;
                case 'pvcName':
                    field.value = pvcCenterDetails.name
                    break;
                case 'totalGoldWeight':
                    field.value = physicalOrder.totalGoldWeight! + ' gm'
                    break;
                case 'totalSilverWeight':
                    field.value = physicalOrder.totalSilverWeight! + ' gm'
                    break;
                case 'transactionDate':
                    field.value = updatedAt ? formatDate(updatedAt) : ''
                    break;
                case 'totalWBMGold':
                    field.value = getThreePointNumber(totalWBM)
                    break;
                case 'totalWAMGold':
                    field.value = getThreePointNumber(totalWAM)
                    break;
                case 'totalWAMTGold':
                    field.value = getThreePointNumber(totalWAMT)
                    break;
                case 'settlementFineWeight':
                    field.value = getThreePointNumber(settlementFineWeight)
                    break;
                case 'totalMetalReturnNetWeight':
                    field.value = getThreePointNumber(totalMetalReturnNetWeight)
                    break;
                case 'totalTaxValue':
                    field.value = getFormattedCurrency(getTwoPointNumber(totalTaxValue))
                    break;
                case 'totalCharges':
                    field.value = getFormattedCurrency(getTwoPointNumber(totalCharges))
                    break;
                case 'roundingRs':
                    field.value = getFormattedCurrency(getTwoPointNumber(roundingRs))
                    break;
                case 'totalValue':
                    field.value = getFormattedCurrency(getTwoPointNumber(physicalOrder.totalPrice || 0))
                    break;
            }
            return field
        })
    }

    const cancelTransactionHandler = () => {
        navigate(PATHS.PVC_TRANSACTIONS)
    }
    
    useEffect(() => {
        if(globalTransactionNumber) getCharges()
    }, [globalTransactionNumber])

    useEffect(() => {
        setPageState(getPageInfo(step as TBuyBackSectionKeys))
        console.log('...', step)
    }, [step])

    useEffect(() => {
        resetOrder()

        if (id) {
            getTransaction()
        }
        else {

            setTransactionInfo({
                currency: CURRENCIES.INR,
                pvcCenterId: user.centerId!,
                status: TRANSACTION_STATUS.INBOUND_INITIATED,
                transactionType: TRANSACTION_TYPES.BUY_BACK,
                weightUOM: WEIGHT_UNITS.GM,
            } as TTransactionBasic)
        }

        return () => resetOrder()
    }, [])

    const PageContent = useCallback(() => {
        switch (step as TBuyBackSectionKeys) {
            case 'INBOUND':
                return <Inbound path={PATHS.BUY_BACK} nextSection='BILLING' nextButtonLable='Next' />

            case 'BILLING':
                return <Billing path={PATHS.BUY_BACK} nextSection='SUMMARY' nextButtonLable='Generate Receipt' prevSection='INBOUND' />

            case 'SUMMARY':
                return <Summary path={PATHS.BUY_BACK} prevSection='BILLING' transitionHistory={[]} summaryDetails={getSummary()} />

            default:
                return <SelectCustomer path={PATHS.BUY_BACK} nextSection='INBOUND' />
        }
    }, [step, inboundDetails])


    return (
        <Container>
            { id ? <HighlightsCardAndCustomerDetails
                        customerConfig={customerConfig}
                        globalTransactionNumber={globalTransactionNumber as string}
                        status={status}
                        inboundId={inboundDetails.id}
                        onSuccessCancelTransaction={cancelTransactionHandler}
                        transactionType={transactionType}
                    /> 
                : <></>
             }
            <PageContent />
        </Container>
    )
}


export default BuyBack